import React from "react";
import {
  Card,
  Skeleton,
  tokens,
  Typography,
} from "@sunrun/experience-ui-components";
import styled from "@emotion/styled/macro";
import { useMediaQuery } from "react-responsive";
import { useParams } from "react-router-dom";
import lidar3dModel from "../../assets/lidar-house-image.svg";
import { useCapContext } from "../../providers/CapContext";
import { CarouselWithThumbs } from "../molecules/CarouselWithThumbs";
import { CapSectionTitle } from "../molecules/CapStepSection";
import { Slide } from "./HomeReadinessStepView";
import { useSiteAuditLidarInfo } from "cap/hooks/useCapSwr";
import { useAppSelector } from "store";
import { mobileMaxWidth } from "components/atoms/GlobalStyles";

const EquipmentLocationStepView: React.FC = () => {
  const { equipmentLocationImages, isCustomer } = useCapContext();
  const isTablet = useMediaQuery({ maxWidth: 1024 });
  const prospectId = useParams().prospectId;
  const authKey = useAppSelector((state) => state?.auth?.hybridToken);
  const { lidarInfo, isLidarInfoLoading } = useSiteAuditLidarInfo(
    prospectId ?? "",
    authKey
  );

  const slides = equipmentLocationImages?.map((image: any, index: number) => (
    <StyledSlide imageUrl={image.sharinpix__ImageURLFull__c} key={index} />
  ));

  const imageUrls =
    equipmentLocationImages?.map(
      (image: any) => image.sharinpix__ImageURLFull__c
    ) ?? [];

  return (
    <>
      <EquipLoc isTablet={isTablet}>
        <EquipmentSidebar isTablet={isTablet}>
          <div>
            <CapSectionTitle>Equipment installation plan</CapSectionTitle>
            <Typography>
              Your installation will require some equipment to be mounted on a
              wall, often near your main electrical panel or electric meter.
              These photos show where the equipment will likely be placed. On
              installation day, your foreperson will review the final locations
              with you.
            </Typography>
          </div>
          {!isCustomer &&
            (lidarInfo?.lidarWebUrl || lidarInfo?.lidarMobileLink) && (
              <StyledCard>
                <StyledLidarContent>
                  <img src={lidar3dModel} alt={"lidar 3d model"} height={70} />
                  <div>
                    <Typography
                      fontWeight="bold"
                      size={18}
                      lineHeight={28}
                      color={tokens.TINTS_HERO_BLUE_10}
                    >
                      LiDAR 3D model
                    </Typography>
                    {isLidarInfoLoading ? (
                      <Skeleton />
                    ) : (
                      <Typography
                        fontWeight="regular"
                        size={14}
                        color={tokens.TINTS_OFF_WHITE_10}
                      >
                        We have the LiDAR 3D model ready.{" "}
                        {lidarInfo?.lidarWebUrl && (
                          <StyledLink
                            rel="noreferrer noopener"
                            href={lidarInfo.lidarWebUrl}
                            target="_blank"
                            aria-label="View web lidar link"
                          >
                            View web
                          </StyledLink>
                        )}
                        {lidarInfo?.lidarMobileLink && (
                          <StyledLink
                            rel="noreferrer noopener"
                            href={lidarInfo.lidarMobileLink}
                            aria-label="View mobile lidar link"
                            target="_blank"
                          >
                            View mobile
                          </StyledLink>
                        )}
                      </Typography>
                    )}
                  </div>
                </StyledLidarContent>
              </StyledCard>
            )}
        </EquipmentSidebar>
        <ImagesContainer>
          {slides && slides.length > 0 && (
            <Card>
              <CarouselWithThumbs slides={slides} imageUrls={imageUrls} />
            </Card>
          )}
        </ImagesContainer>
      </EquipLoc>
    </>
  );
};

const EquipLoc = styled.div<{ isTablet: boolean }>`
  display: ${({ isTablet }) => (isTablet ? "inherit" : "grid")};
  grid-template-columns: 30% calc(70% - 32px);
  margin-top: ${({ isTablet }) => (isTablet ? "0" : "16px")};
  gap: 32px;
  padding: ${({ isTablet }) => (isTablet ? "0" : "16px 0")};
  min-height: 585px;

  @media (max-width: ${mobileMaxWidth}px) {
    grid-template-columns: 100%;
  }
`;

const EquipmentSidebar = styled.div<{ isTablet: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 12px;
  flex-basis: 0;
  padding-bottom: ${({ isTablet }) => (isTablet ? "24px" : "0")};
`;

const ImagesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const StyledSlide = styled(Slide)`
  width: var(--slide-size);
`;

const StyledCard = styled(Card)`
  display: flex;
  max-height: 110px;

  > div {
    margin: 16px !important;
  }
`;

const StyledLidarContent = styled.div`
  display: flex;
  gap: 9px;
  align-items: center;
  > img {
    border-radius: 10px;
  }
`;

const StyledLink = styled.a`
  margin-right: 4px;
  color: ${tokens.TINTS_HERO_BLUE_50};
  font-weight: 500;
`;

export { EquipmentLocationStepView };
